import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { baseUrl } from '../Utils';

interface User {
    name: string;
    email: string;
    role: string;
    profile_image: string | null;
}

interface AuthContextType {
    user: User | null;
    role: string | null;
    permissions: string[];
    isLoading: boolean;
    hasPermission: (permission: string) => boolean;
    hasRole: (roleName: string) => boolean;
    setAuthData: (role: string, permissions: string[], user: User) => void;
    clearAuthData: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [role, setRole] = useState<string | null>(null);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Fetch auth data from backend on mount if token exists
        const token = localStorage.getItem('token');
        if (token) {
            fetchAuthData();
        } else {
            setIsLoading(false);
        }
    }, []);

    const fetchAuthData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setIsLoading(false);
                return;
            }

            // Fetch user profile with permissions from backend
            const response = await axios.get(`${baseUrl}me`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data) {
                setUser({
                    name: response.data.name,
                    email: response.data.email,
                    role: response.data.role,
                    profile_image: response.data.profile_image
                });
            }

            if (response.data.role) {
                setRole(response.data.role);
            }
            if (response.data.permissions) {
                setPermissions(response.data.permissions);
            }
        } catch (error) {
            console.error('Failed to fetch auth data:', error);
            // If token is invalid, clear everything
            clearAuthData();
        } finally {
            setIsLoading(false);
        }
    };

    const setAuthData = (newRole: string, newPermissions: string[], newUser: User) => {
        setRole(newRole);
        setPermissions(newPermissions);
        setUser(newUser);
        // No need to store in localStorage anymore
    };

    const clearAuthData = () => {
        setRole(null);
        setUser(null);
        setPermissions([]);
        localStorage.removeItem('token');
        localStorage.removeItem('login');
    };

    const hasPermission = (permission: string): boolean => {
        return permissions.includes(permission);
    };

    const hasRole = (roleName: string): boolean => {
        return role === roleName;
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                role,
                permissions,
                isLoading,
                hasPermission,
                hasRole,
                setAuthData,
                clearAuthData,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
